import { MessageSquare, Pencil } from 'lucide-react'

import { useSmsGatewaysTable } from '@/hooks/features/sms-gateways/useSmsGatewaysTable'
import { useDataTable } from '@/hooks/ui'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DataTable } from '@/components/ui/data-table'

import { PageHeader, StatusBadge } from '@/components/atoms'
import { SmsGatewayModel } from '@/types/model/SmsGateway'

import { SmsGatewayDialog } from './SmsGatewayDialog'

type TableColumns = 'gateway' | 'endpoint' | 'mode' | 'status' | 'actions'

const visibleColumns: TableColumns[] = ['gateway', 'endpoint', 'mode', 'status', 'actions']

export const SmsGatewaysTable = () => {
    const {
        loading,
        data,
        gatewayDialogOpen,
        editGateway,
        updateLoading,
        openEditDialog,
        closeDialog,
        handleUpdateGateway,
    } = useSmsGatewaysTable()

    const { columns } = useDataTable<SmsGatewayModel.Gateway, TableColumns>({
        columns: [
            {
                accessorKey: 'gateway',
                columnLabel: () => 'Gateway',
                cell: (gateway) => (
                    <div className="flex items-center gap-3">
                        <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border-subtle bg-subdued">
                            <MessageSquare className="size-5 text-muted-foreground" />
                        </div>
                        <div>
                            <p className="font-medium text-ink">{gateway.name}</p>
                            <p className="text-xs text-text-2">{gateway.key}</p>
                        </div>
                    </div>
                ),
            },
            {
                accessorKey: 'endpoint',
                columnLabel: () => 'API endpoint',
                cell: (gateway) => <span className="block max-w-48 truncate text-text-2">{gateway.api_endpoint}</span>,
            },
            {
                accessorKey: 'mode',
                columnLabel: () => 'Mode',
                cell: (gateway) => (
                    <Badge tone={gateway.credential_type === 'sandbox' ? 'warning' : 'soft'} className="capitalize">
                        {gateway.credential_type || 'production'}
                    </Badge>
                ),
            },
            {
                accessorKey: 'status',
                columnLabel: () => 'Status',
                cell: (gateway) => <StatusBadge type="common" status={gateway.status} />,
            },
            {
                accessorKey: 'actions',
                columnLabel: () => 'Actions',
                cell: (gateway) => (
                    <Button variant="ghost" size="icon" aria-label="Edit" onClick={() => openEditDialog(gateway)}>
                        <Pencil className="text-muted-foreground" />
                    </Button>
                ),
                classes: () => ({
                    column: 'text-right',
                    cell: 'text-right',
                }),
                isAction: true,
            },
        ],
        visibleColumns,
    })

    return (
        <div>
            <PageHeader size="large" title="SMS" />

            <Card>
                <CardHeader>
                    <CardTitle>SMS providers</CardTitle>
                    <CardDescription>
                        {data.length} {data.length === 1 ? 'gateway' : 'gateways'},{' '}
                        {data.filter((g) => g.status === 'active').length} active,{' '}
                        {data.filter((g) => g.is_configured).length} configured
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <DataTable columns={columns} data={data} loading={loading} />
                </CardContent>
            </Card>

            {editGateway && (
                <SmsGatewayDialog
                    open={gatewayDialogOpen}
                    onOpenChange={closeDialog}
                    gateway={editGateway}
                    onSave={handleUpdateGateway}
                    loading={updateLoading}
                />
            )}
        </div>
    )
}
